import { Controller } from "@hotwired/stimulus"

// Homepage additions share the established landing-page pricing controller.
export default class extends Controller {
  static targets = ["demoTab", "demoPanel", "pricingSelect", "proofDialog", "proofImage"]

  connect() {
    this.initializeTabs(this.demoTabTargets, this.demoPanelTargets, "demoKey")
    this.initializeTabs(this.pricingTabs, this.pricingPanels, "pricingKey")
    this.updatePricingSelect()
  }

  get pricingTabs() {
    return Array.from(this.element.querySelectorAll('[data-landing-page-target~="pricingTab"]'))
  }

  get pricingPanels() {
    return Array.from(this.element.querySelectorAll('[data-landing-page-target~="pricingPanel"]'))
  }

  initializeTabs(tabs, panels, key) {
    const selected = tabs.find((tab) => tab.getAttribute("aria-selected") === "true" ||
      tab.classList.contains("is-active")) || tabs[0]
    tabs.forEach((tab) => {
      const panel = panels.find((candidate) => candidate.dataset[key] === tab.dataset[key])
      if (!panel) return
      tab.id ||= `public-home-${key}-${tab.dataset[key]}-tab`
      panel.id ||= `public-home-${key}-${tab.dataset[key]}-panel`
      tab.setAttribute("role", "tab")
      tab.setAttribute("aria-controls", panel.id)
      panel.setAttribute("role", "tabpanel")
      panel.setAttribute("aria-labelledby", tab.id)
    })
    if (selected) this.activateTab(tabs, panels, key, selected.dataset[key])
  }

  activateTab(tabs, panels, key, selected) {
    tabs.forEach((tab) => {
      const active = tab.dataset[key] === selected
      tab.classList.toggle("is-active", active)
      tab.setAttribute("aria-selected", String(active))
      tab.tabIndex = active ? 0 : -1
    })
    panels.forEach((panel) => {
      const active = panel.dataset[key] === selected
      panel.classList.toggle("is-active", active)
      panel.hidden = !active
    })
  }

  switchDemo(event) {
    this.activateTab(this.demoTabTargets, this.demoPanelTargets, "demoKey", event.currentTarget.dataset.demoKey)
  }

  navigateDemo(event) {
    this.navigateTabs(event, this.demoTabTargets, event.currentTarget)
  }

  navigatePricing(event) {
    const tab = event.target.closest('[data-landing-page-target~="pricingTab"]')
    if (tab) this.navigateTabs(event, this.pricingTabs, tab)
  }

  navigateTabs(event, tabs, current) {
    const index = tabs.indexOf(current)
    const destinations = {
      ArrowRight: (index + 1) % tabs.length,
      ArrowLeft: (index - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1
    }
    if (!(event.key in destinations)) return
    event.preventDefault()
    const next = tabs[destinations[event.key]]
    next.focus()
    next.click()
  }

  selectPricing() {
    this.pricingTabs.find((tab) => tab.dataset.pricingKey === this.pricingSelectTarget.value)?.click()
  }

  syncPricing(event) {
    const tab = event.target.closest('[data-landing-page-target~="pricingTab"]')
    if (!tab) return
    this.activateTab(this.pricingTabs, this.pricingPanels, "pricingKey", tab.dataset.pricingKey)
    this.updatePricingSelect()
  }

  updatePricingSelect() {
    if (!this.hasPricingSelectTarget) return
    const selected = this.pricingTabs.find((tab) => tab.getAttribute("aria-selected") === "true")
    if (selected) this.pricingSelectTarget.value = selected.dataset.pricingKey
  }

  // Proof viewer — deliberately a native <dialog>, not landing-page#openLightbox.
  // showModal() gives the focus trap, inert background, Escape handling and
  // ::backdrop natively, and `close` lets us hand focus back to the trigger;
  // the legacy div.landing-lightbox (class toggle + body overflow hack, no
  // role/aria-modal, no focus management) has none of that and stays only for
  // the affiliate composition until #12401 converges both onto this pattern.
  openProof(event) {
    if (!this.hasProofDialogTarget || !this.hasProofImageTarget) return
    const trigger = event.currentTarget
    if (!trigger.dataset.proofSrc) return
    this.proofTrigger = trigger
    this.proofImageTarget.src = trigger.dataset.proofSrc
    this.proofImageTarget.alt = trigger.dataset.proofAlt || ""
    this.proofDialogTarget.showModal()
  }

  closeProof() {
    if (this.hasProofDialogTarget) this.proofDialogTarget.close()
  }

  closeProofBackdrop(event) {
    if (event.target !== this.proofDialogTarget) return
    const rect = this.proofDialogTarget.getBoundingClientRect()
    if (event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom) this.closeProof()
  }

  restoreProofFocus() {
    if (this.hasProofImageTarget) this.proofImageTarget.removeAttribute("src")
    if (this.proofTrigger?.isConnected) this.proofTrigger.focus()
    this.proofTrigger = null
  }
}
